import { useFormik } from 'formik'
import { useSelector, useDispatch } from 'react-redux'
import * as Yup from 'yup'
import {
  useAddChecklistMutation,
  useDeleteChecklistMutation,
  useLazyGetTaskDetailQuery,
} from 'apiv2'
import { prepareCommonSelects } from 'helpers/prepares'
import { allTasksSelector } from 'store/projects'
import { getTaskDetailAction } from 'store/tasks'
import Button from 'ui/components/buttons/Button'
import { ButtonGroup } from 'ui/components/buttons/Button/styled'
import Select from 'ui/components/forms/Select'
import ModalBasic from 'ui/components/ModalBasic'
import { notifyError, notifySuccess } from 'ui/components/Notify'
import {
  ChecklistTransferModalTitle,
  ChecklistTransferModalText,
} from './styled'
import { CHECKLIST_TRANSFER_TYPE, MAX_CHECKLISTS } from '../../constants'
import {
  toFormikChecklists,
  prepareChecklistPointsForTransfer,
} from '../../helpers'

const ChecklistTransferModal = ({
  type,
  open,
  checklist,
  defaultTaskId,
  onMoveSuccess,
  handlerClose,
}) => {
  const dispatch = useDispatch()
  const taskList = useSelector(allTasksSelector)
  const [addChecklist] = useAddChecklistMutation()
  const [deleteChecklist] = useDeleteChecklistMutation()
  const [triggerGetTaskDetail] = useLazyGetTaskDetailQuery()

  const isCopyMode = type === CHECKLIST_TRANSFER_TYPE.COPY
  const isMoveMode = type === CHECKLIST_TRANSFER_TYPE.MOVE
  const destinationTasks = taskList.filter((task) => task.id !== defaultTaskId)

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      task: null,
    },
    validationSchema: Yup.object({
      task: Yup.object({
        id: Yup.number().required(),
        value: Yup.string().required(),
        label: Yup.string().required(),
      })
        .required('Нужно заполнить')
        .typeError('Нужно заполнить'),
    }),
    onSubmit: async (values, { resetForm }) => {
      const targetTaskId = values.task.id
      const checlistId = checklist.id

      const targetTask = await triggerGetTaskDetail({
        id: targetTaskId,
      }).unwrap()
      const targetChecklists = targetTask?.checklist
      const isChecklistLimitReached =
        targetChecklists?.length === MAX_CHECKLISTS

      if (isChecklistLimitReached) {
        notifyError(
          `Нельзя ${isCopyMode ? 'скопировать' : 'переместить'} чек-лист: в целевой задаче достигнут лимит чек-листов`
        )
      } else {
        const mergedItems = [
          ...toFormikChecklists(targetChecklists),
          ...prepareChecklistPointsForTransfer(checklist),
        ]
        if (isCopyMode) {
          try {
            await addChecklist({
              taskId: targetTaskId,
              data: {
                items: mergedItems,
              },
            }).unwrap()
            notifySuccess('Чек-лист скопирован')
          } catch (error) {
            notifyError('Не удалось скопировать чек-лист')
          } finally {
            handlerClose()
          }
        }
        if (isMoveMode) {
          try {
            await addChecklist({
              taskId: targetTaskId,
              data: {
                items: mergedItems,
              },
            }).unwrap()
            if (Boolean(checklist?.id)) {
              await deleteChecklist({
                itemId: checlistId,
              }).unwrap()
            }
            notifySuccess('Чек-лист перемещён')
            dispatch(getTaskDetailAction(defaultTaskId))
            onMoveSuccess()
          } catch (error) {
            notifyError('Не удалось переместить чек-лист')
          } finally {
            handlerClose()
          }
        }
        resetForm(defaultTaskId)
      }
    },
  })

  return (
    <ModalBasic open={open} handlerClose={handlerClose}>
      <ChecklistTransferModalTitle>
        {isCopyMode ? 'Копировать чек-лист' : 'Переместить чек-лист'}
      </ChecklistTransferModalTitle>
      <ChecklistTransferModalText>
        Выберите задачу, в которую нужно{' '}
        {isCopyMode ? 'скопировать' : 'перенести'} чек-лист.
      </ChecklistTransferModalText>
      <form onSubmit={formik.handleSubmit} noValidate>
        <Select
          label="Задача"
          placeholder="Не выбрана"
          options={prepareCommonSelects(destinationTasks, true)}
          value={
            formik.getFieldMeta('task')
              ? formik.getFieldMeta('task').value
              : null
          }
          onChange={(newValue) => {
            if (newValue) {
              formik.setFieldValue('task', newValue)
            } else {
              formik.setFieldValue('task', null)
            }
          }}
          meta={formik.getFieldMeta('task')}
          error={
            formik.getFieldMeta('task').touched &&
            formik.getFieldMeta('task').error
          }
          maxHeight={300}
        />

        <ButtonGroup>
          <Button
            title={isCopyMode ? 'Копировать' : 'Переместить'}
            type="submit"
          />
          <Button
            title="Отмена"
            theme="borderGrey"
            type="reset"
            onClick={() => {
              handlerClose()
              formik.resetForm()
            }}
          />
        </ButtonGroup>
      </form>
    </ModalBasic>
  )
}

export default ChecklistTransferModal
